/*
 * The privacy policy, bundled and parsed — the CHANGELOG pattern applied to a
 * legal text (see docs/VOICE_CHAT.md, "Privacy policy" → "Where it lives").
 *
 * `privacy.md` beside this file is the only copy. It is imported at BUILD time
 * for the same reason the changelog is: local play works with no network, and
 * the file the build turns into `/privacy.html` (vite.config.ts) is then the
 * file the player reads in the app — there is no second copy to drift.
 *
 * The parsing lives in parsePrivacy.ts, not here, because this module imports
 * the text with `?raw` and the Vite config cannot load anything that does.
 *
 * **Two "changes" in one file, kept apart on purpose.** The design says a
 * material update is shown "headed with what changed (one line per change, from
 * a `## Changes` section at the bottom of the file)". But the policy itself
 * already ends with a section called `## Changes` — the one telling the reader
 * how we will announce changes — and that is policy text a player must see. A
 * parser that took `## Changes` as its list would swallow it (or, worse, a
 * later edit to that paragraph would turn up as a "what changed" line). So the
 * list is under its own heading, `## What changed in this version`, which no
 * policy would ever use for prose; it sits last in the file, is empty for
 * version 1, and is cut out of the body before it is rendered. A line written
 * under it that is not a bullet is reported as a problem rather than adopted.
 */
import raw from "./privacy.md?raw";
import { parsePrivacy } from "./parsePrivacy";

export { parsePrivacy } from "./parsePrivacy";
export type { PrivacyPolicy } from "./parsePrivacy";

const policy = parsePrivacy(raw);

/** The bundled policy, parsed once. `problems` is empty unless the file is malformed. */
export const PRIVACY = policy;
/** Moves on every edit to the policy. */
export const PRIVACY_VERSION = policy.version;
/** Moves only on a material change: what re-acknowledgement is keyed to. */
export const PRIVACY_MATERIAL_VERSION = policy.materialVersion;
/** `YYYY-MM-DD`. */
export const PRIVACY_EFFECTIVE = policy.effective;
/** Still awaiting legal review. True too if the status line cannot be read. */
export const PRIVACY_IS_DRAFT = policy.status !== "final";
/** The policy text, header and change list removed. */
export const PRIVACY_BODY = policy.body;
/** One line per change in this version; empty for version 1. */
export const PRIVACY_CHANGES = policy.changes;

/**
 * Whether the app offers the policy at all — the home-screen link and the row
 * in Settings both ask this.
 *
 * A draft never reaches a production player: it still has `[placeholders]` in
 * it and says things legal review may change, and a player who read it would
 * reasonably take it as what we promise. Dev and local builds show it, which is
 * where it gets read before it is final.
 *
 * Parameters for the test; the app calls it bare.
 */
export function privacyLinkVisible(
  isDraft: boolean = PRIVACY_IS_DRAFT,
  appEnv: string | undefined = import.meta.env.VITE_APP_ENV,
): boolean {
  return !(isDraft && appEnv === "prod");
}
